import { useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal, ModalBody, ModalFooter } from "flowbite-react";
import { OnboardingCard } from "@/features/card/components/OnboardingModal/OnBoardingModalElements/OnboardingCard";
import { OnboardingModalContent } from "@/features/card/components/OnboardingModal/OnBoardingModalElements/OnboardingModalContent";
import { OnboardingModalFooter } from "@/features/card/components/OnboardingModal/OnBoardingModalElements/OnboardingModalFooter";
import { OnboardingModalHeader } from "@/features/card/components/OnboardingModal/OnBoardingModalElements/OnboardingModalHeader";
import { CardActivationSlideContent } from "@/features/card/components/OnboardingModal/OnBoardingModalElements/OnboardingModalSteps";
import { useCardEvents } from "@/features/card/hooks/useCardEvents";
import { useCardMutations } from "@/features/card/hooks/useCardMutations";
import { useCards } from "@/features/card/hooks/useCards";
import { type CardActivationFormData, cardActivationFormSchema } from "@/features/card/schemas/forms/cardActivationForm.schema";
import { useYeatiesConsent } from "@/features/purchasePoints";
import { useOnboardingContext } from "@/shared/context/OnboardingContext";
import { cn } from "@/shared/utils/style.util";

export type ModalSteps = "new-card" | "card-activation" | "card-success";

type OnboardingModalProps = {
  isOpen: boolean
  onClose: () => void
  cardId?: string
};

export const OnboardingModal = ({ isOpen, onClose, cardId }: OnboardingModalProps) => {
  const { cards, unactivatedCard } = useCards();
  const { activateCard, isUpdating } = useCardMutations();
  const { startCardActivation, completeCardActivation } = useOnboardingContext();
  const [modalStep, setModalStep] = useState<ModalSteps>("new-card");
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [targetCardId, setTargetCardId] = useState<string | null>(null);
  const [isYeatiesChecked, setIsYeatiesChecked] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const resolvedTargetCardId = targetCardId || cardId || unactivatedCard?.id || null;
  const targetCard = resolvedTargetCardId ? cards.find((card) => card.id === resolvedTargetCardId) : undefined;

  const methods = useForm<CardActivationFormData>({
    resolver: zodResolver(cardActivationFormSchema),
    defaultValues: { public_token: "" },
    mode: "onSubmit",
  });

  const { handleSubmit, reset } = methods;

  const scrollToTop = () => {
    modalRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const yeaties = useYeatiesConsent();

  const onOpenModal = () => {
    yeaties.reset();
    setIsYeatiesChecked(false);
    setModalStep("new-card");
    setIsCardFlipped(false);
    reset();
    // Lock the initial target card id so effects keep tracking the same card across refetches
    setTargetCardId(cardId || unactivatedCard?.id || null);
  };

  const onFlipCard = () => {
    setIsCardFlipped(!isCardFlipped);
  };

  const onBackToWelcome = () => {
    reset();
    setActivationError(null);
    setIsCardFlipped(false);
    setModalStep("new-card");
    scrollToTop();
  };

  const sendYeatiesAnswer = () => {
    if (!yeaties.isOffered) return;
    if (isYeatiesChecked) yeaties.accept();
    else yeaties.refuse();
  };

  const onActivationInProgress = () => {
    sendYeatiesAnswer();
    setIsCardFlipped(false);
    setModalStep("card-success");
    scrollToTop();
  };

  const onWrongActivationCode = () => {
    setModalStep("card-activation");
    reset();
    setActivationError("Le code d'activation que vous avez saisi semble incorrect, veuillez réessayer.");
  };

  const onActivationFailed = () => {
    setIsCardFlipped(false);
    reset();
    setModalStep("card-activation");
    setActivationError("L'activation de votre carte semble avoir échoué. Veuillez réessayer.");
  };

  const onActivateCard = async (data: CardActivationFormData) => {
    if (!targetCard || !data.public_token) return;

    setActivationError(null);

    try {
      await activateCard({ cardId: targetCard.id, data });
      startCardActivation(targetCard.id);
      onActivationInProgress();
    }
    catch {
      onWrongActivationCode();
    }
  };

  const onPusherResponse = (success: boolean) => {
    // On failure, immediately revert optimistic activation and show error
    if (!success) {
      completeCardActivation();
      onActivationFailed();
    }
    // On success, do not clear optimistic state here.
    // Wait for query refetch to propagate activated card, then clear in the effect below.
  };
  useCardEvents(targetCard?.id || "", onPusherResponse);

  useEffect(() => {
    if (isOpen) onOpenModal();
  }, [isOpen]);

  // When the target card becomes activated from refetched data,
  // clear the optimistic activation state.
  useEffect(() => {
    if (targetCard?.is_live === 1) {
      completeCardActivation();
    }
  }, [targetCard?.is_live, completeCardActivation]);

  if (!targetCard) return null;

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      size="2xl"
      className="onboarding-modal"
      dismissible={false}
    >
      <FormProvider {...methods}>
        <ModalBody className="relative overflow-y-auto overflow-x-hidden" ref={modalRef}>
          <div className="flex flex-col justify-between items-center">
            <OnboardingModalHeader modalStep={modalStep} isCardFlipped={isCardFlipped} activationError={activationError} consent={yeaties.consent} />
            <OnboardingCard
              card={targetCard}
              isFlipped={isCardFlipped}
              hasActivationError={!!activationError}
              hasSheen={modalStep === "card-success" && yeaties.consent === "accepted"}
            />
            <OnboardingModalContent
              modalStep={modalStep}
              yeaties={yeaties}
              isYeatiesChecked={isYeatiesChecked}
              onYeatiesCheckedChange={setIsYeatiesChecked}
            />
          </div>
        </ModalBody>

        <ModalFooter className="flex justify-center border-t-0 pt-0">
          <div
            className={cn(
              "w-full",
              modalStep === "card-activation" && isCardFlipped ? "card-activation-footer" : "flex gap-3",
            )}
          >
            <OnboardingModalFooter
              modalStep={modalStep}
              isCardFlipped={isCardFlipped}
              isUpdating={isUpdating}
              onClose={onClose}
              onBackToWelcome={onBackToWelcome}
              onFlipCard={onFlipCard}
              onActivateCard={handleSubmit(onActivateCard)}
              onUseCard={onClose}
              onSetModalStep={setModalStep}
            />
            {modalStep === "card-activation" && isCardFlipped && (
              <div className="[grid-area:code]">
                <CardActivationSlideContent />
              </div>
            )}
          </div>
        </ModalFooter>
      </FormProvider>
    </Modal>
  );
};
